import React, { useState, useCallback, useMemo, memo } from "react";
import { View, StyleSheet, Dimensions } from "react-native";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Modal,
} from "@mui/material";
import { useSelector } from "react-redux";
import AppScreenBackground from "../components/Common/ComponentAppBackground.jsx";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import moment from "moment";

import { translateText } from "../translations/translationService";
import { ImageComponent, InfoComponent } from "../components/FullComponent.js";
import { parseDurationToMinutes } from "../services/Functions.js";
import { TIME_AND_STATUS_SCREEN_COLORS } from "../style/colors.js";

const { width } = Dimensions.get("window");

const ICON_CONFIG = {
  WEIGHT: { name: "weight", size: 22, color: TIME_AND_STATUS_SCREEN_COLORS.ICON_WEIGHT },
  TIME_WORK: { name: "clock-outline", size: 22, color: TIME_AND_STATUS_SCREEN_COLORS.ICON_TIME_WORK },
  PROBLEM_QUALITY: { name: "alert-circle", size: 22, color: TIME_AND_STATUS_SCREEN_COLORS.ICON_PROBLEM_QUALITY },
  CRIMPING: { name: "ring", size: 22, color: TIME_AND_STATUS_SCREEN_COLORS.ICON_CRIMPING },
  default: { name: "chart-donut", size: 20, color: TIME_AND_STATUS_SCREEN_COLORS.ICON_DEFAULT },
};

const getIconConfig = (tipoTiempo) =>
  ICON_CONFIG[tipoTiempo] || ICON_CONFIG.default;

const TimeAndStatusScreen = ({ route }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  const lang = useSelector((state) => state.lang.value);
  const team = useSelector((state) => state.user.team);

  const data = route.params.infoData;

  const fabricaData = useMemo(
    () =>
      data.eFabrica.map((item) => ({
        descripcion: item.descripcion,
        numeropiedras: Number(item.numeropiedras).toFixed(2),
      })),
    [data.eFabrica]
  );

  const tiemposConNumeropiedras = useMemo(
    () =>
      data.tiempos.map((tiempo) => {
        const fabricaItem = fabricaData.find(
          (f) => f.descripcion === tiempo.tipo
        );
        return {
          ...tiempo,
          numeropiedras: fabricaItem ? fabricaItem.numeropiedras : null,
        };
      }),
    [data.tiempos, fabricaData]
  );

  const arrayDataTime = useMemo(() => {
    const processedData = tiemposConNumeropiedras.map((obj) => {
      const {
        usuario,
        team,
        tipo,
        fecha_ini,
        fecha_fin,
        duracion,
        peso,
        tipo_tiempo,
        cantidad_proceso,
        numeropiedras,
        incidencia,
      } = obj;

      const StartDate = moment(fecha_ini?.date);
      const EndDate = fecha_fin?.date ? moment(fecha_fin.date) : null;

      return {
        operario: usuario || "",
        equipo: team || "",
        tipo: tipo || "",
        inicio: StartDate.isValid() ? StartDate.format("DD/MM/YY HH:mm") : "-",
        fin:
          EndDate && EndDate.isValid() ? EndDate.format("DD/MM/YY HH:mm") : "-",
        duracion: duracion?.date
          ? moment(duracion.date, "YYYY-MM-DD HH:mm:ss").format("HH:mm:ss")
          : "-",
        peso: peso ? Number(peso).toFixed(2) : "-",
        cantidad: cantidad_proceso ? Number(cantidad_proceso).toFixed(1) : "-",
        tipo_tiempo: tipo_tiempo || "-",
        fecha_ini: StartDate,
        numeropiedras: numeropiedras || "-",
        incidencia: incidencia || "-",
      };
    });

    return processedData.sort((b, a) => a.fecha_ini - b.fecha_ini);
  }, [tiemposConNumeropiedras]);

  const uniqueTeams = useMemo(
    () => Array.from(new Set(arrayDataTime.map((item) => item.equipo))),
    [arrayDataTime]
  );

  const filteredArrayDataTime = useMemo(
    () =>
      selectedTeam
        ? arrayDataTime.filter((item) => item.equipo === selectedTeam)
        : arrayDataTime,
    [arrayDataTime, selectedTeam]
  );

  const handleTeamSelection = useCallback((team) => {
    setSelectedTeam(team);
    setShowTeamDropdown(false);
  }, []);

  const closeTeamDropdown = useCallback(() => {
    setShowTeamDropdown(false);
  }, []);

  const TableRowComponent = memo(({ item, index }) => {
    const iconConfig = getIconConfig(item.tipo_tiempo);
    const hasIncidence = Number(item.incidencia) > 0;
    const isTerminated = item.tipo === "Term.";

    const backgroundColor = hasIncidence
      ? TIME_AND_STATUS_SCREEN_COLORS.STATUS_ERROR
      : isTerminated
      ? TIME_AND_STATUS_SCREEN_COLORS.STATUS_SUCCESS
      : "inherit";

    const iconColor =
      hasIncidence || isTerminated ? TIME_AND_STATUS_SCREEN_COLORS.BACKGROUND_WHITE : iconConfig.color;

    const calculatedStandard = useMemo(() => {
      const cantidadNum = parseFloat(item.cantidad);
      const numeropiedrasNum = parseFloat(item.numeropiedras);
      return Number.isFinite(cantidadNum) && Number.isFinite(numeropiedrasNum)
        ? (cantidadNum * numeropiedrasNum).toFixed(2)
        : "-";
    }, [item.cantidad, item.numeropiedras]);

    const formattedDuration = useMemo(() => {
      const duration = parseDurationToMinutes(item.duracion);
      return duration ? duration.toFixed(2) : "-";
    }, [item.duracion]);

    return (
      <TableRow key={index} sx={{ backgroundColor }}>
        <TableCell align='center'>
          <MaterialCommunityIcons
            name={iconConfig.name}
            size={iconConfig.size}
            color={iconColor}
          />
        </TableCell>
        <TableCell align='center'>{item.operario}</TableCell>
        <TableCell align='center'>{item.equipo}</TableCell>
        <TableCell align='center'>{item.tipo}</TableCell>
        <TableCell align='center'>{item.inicio}</TableCell>
        <TableCell align='center'>{item.fin}</TableCell>
        <TableCell align='center'>{formattedDuration}</TableCell>
        <TableCell align='center'>{item.peso}</TableCell>
        <TableCell align='center'>{item.cantidad}</TableCell>
        <TableCell align='center'>{calculatedStandard}</TableCell>
      </TableRow>
    );
  });

  const TableComponent = memo(() => {
    const headers = [
      "Icono",
      "Operario",
      "Equipo",
      "Tipo",
      "Inicio",
      "Fin",
      "Min. Real",
      "Peso",
      "Unidades",
      "Standard",
    ];

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: TIME_AND_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER }}>
              {headers.map((header) => (
                <TableCell
                  key={header}
                  align='center'
                  sx={{ backgroundColor: `${TIME_AND_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER} !important` }}>
                  {translateText(header, lang)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredArrayDataTime.map((item, index) => (
              <TableRowComponent
                key={`${item.operario}-${index}`}
                item={item}
                index={index}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  });

  const TeamSelectionModal = memo(() => (
    <Modal open={showTeamDropdown} onClose={closeTeamDropdown}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: TIME_AND_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
          padding: 2,
          borderRadius: 2,
          width: 400,
          maxHeight: 500,
          display: "flex",
          flexDirection: "column",
        }}>
        <Typography
          variant='h6'
          color='white'
          sx={{ mb: 2, textAlign: "center" }}>
          {translateText("Seleccionar Equipo", lang)}
        </Typography>

        <Box sx={{ flexGrow: 1, maxHeight: 300, overflowY: "auto", mb: 2 }}>
          <Button
            fullWidth
            onClick={() => handleTeamSelection(null)}
            sx={{
              textAlign: "center",
              color: "white",
              mb: 1,
              backgroundColor:
                selectedTeam === null ? "rgba(255,255,255,0.1)" : "transparent",
            }}>
            {translateText("Todos los equipos", lang)}
          </Button>

          {uniqueTeams.map((teamName) => (
            <Button
              fullWidth
              key={teamName}
              onClick={() => handleTeamSelection(teamName)}
              sx={{
                textAlign: "center",
                color: "white",
                mb: 1,
                backgroundColor:
                  selectedTeam === teamName
                    ? "rgba(255,255,255,0.1)"
                    : "transparent",
              }}>
              {translateText(teamName, lang)}
            </Button>
          ))}
        </Box>

        <Button
          onClick={closeTeamDropdown}
          fullWidth
          sx={{
            backgroundColor: TIME_AND_STATUS_SCREEN_COLORS.STATUS_ACTIVE,
            color: "white",
            "&:hover": { backgroundColor: TIME_AND_STATUS_SCREEN_COLORS.STATUS_ACTIVE_HOVER },
          }}>
          {translateText("Cerrar", lang)}
        </Button>
      </Box>
    </Modal>
  ));

  return (
    <AppScreenBackground style={styles.container}>
      <View style={styles.content}>
        <View style={styles.infoSection}>
          <View style={styles.infoColumn}>
            <InfoComponent data={data} />
          </View>
          <View style={[styles.infoColumn, styles.imageColumn]}>
            <ImageComponent data={data} />
          </View>
        </View>

        <View style={styles.tableSection}>
          <TableComponent />
        </View>
      </View>

      <TeamSelectionModal />
    </AppScreenBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: 10,
  },
  infoSection: {
    flexDirection: "row",
    marginBottom: 10,
  },
  infoColumn: {
    flex: 1,
  },
  imageColumn: {
    marginLeft: 20,
  },
  tableSection: {
    flex: 1,
    minHeight: 300,
  },
});

export default memo(TimeAndStatusScreen);
